import { makeStyles } from "@material-ui/core/styles";
import AvailabilityCard from "@saleor/components/AvailabilityCard";
import CardSpacer from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Grid from "@saleor/components/Grid";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import SingleAutocompleteSelectField from "@saleor/components/SingleAutocompleteSelectField";
import { ProductErrorWithAttributesFragment } from "@saleor/fragments/types/ProductErrorWithAttributesFragment";
import { TaxTypeFragment } from "@saleor/fragments/types/TaxTypeFragment";
import { WarehouseFragment } from "@saleor/fragments/types/WarehouseFragment";
import useDateLocalize from "@saleor/hooks/useDateLocalize";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import { maybe } from "@saleor/misc";
import { Brand } from "@saleor/products/customTypes/Brand";
import { ProductsUser } from "@saleor/products/customTypes/ProductsUser";
import { TenantDropdownItem } from "@saleor/products/customTypes/TenantDropdownItem";
import { ProductVariant_productVariant_stockOnMall } from "@saleor/products/types/ProductVariant";
import { SearchCategories_search_edges_node } from "@saleor/searches/types/SearchCategories";
import { SearchCollections_search_edges_node } from "@saleor/searches/types/SearchCollections";
import { SearchProductTags_search_edges_node } from "@saleor/searches/types/SearchProductTags";
import { StaffTenantAssignment_tenant_mall } from "@saleor/staff/types/StaffTenantAssignment";
import { FetchMoreProps, ListActions, ReorderAction } from "@saleor/types";
// import { convertFromRaw, RawDraftContentState } from "draft-js";
import { RawDraftContentState } from "draft-js";
import React, { useEffect } from "react";
import { useIntl } from "react-intl";

import {
  ProductDetails_product,
  ProductDetails_product_images,
  ProductDetails_product_variants
} from "../../types/ProductDetails";
import { getChoices, ProductUpdatePageFormData } from "../../utils/data";
import ProductAttributes, { ProductAttributeInput } from "../ProductAttributes";
import ProductDeepLink from "../ProductDeepLink";
import ProductDetailsForm from "../ProductDetailsForm";
import ProductDimension from "../ProductDimensions";
import ProductImages from "../ProductImages";
import ProductKAMSDropdownGroup from "../ProductKAMSDropdownGroup";
import ProductOrganization from "../ProductOrganization";
import ProductPricing from "../ProductPricing";
// import ProductShipping from "../ProductShipping/ProductShipping";
import ProductStatus from "../ProductStatus";
import ProductStocks, { ProductStockInput } from "../ProductStocks";
import ProductTaxes from "../ProductTaxes";
import ProductVariants from "../ProductVariants";
import ProductUpdateForm from "./form";

const useStyles = makeStyles(
  theme => ({
    tenantDropdown: {
      margin: theme.spacing(3, 0)
    }
  }),
  { name: "ProductUpdatePage" }
);

export interface ProductUpdatePageProps extends ListActions {
  brand?: string;
  brands?: Brand[];
  brandLabel?: string;
  onBrandChange?: (e) => void;
  userView?: string;
  userViews?: any;
  userViewLabel?: string;
  onUserViewChange?: (e) => void;
  defaultWeightUnit: string;
  errors: ProductErrorWithAttributesFragment[];
  placeholderImage: string;
  collections: SearchCollections_search_edges_node[];
  categories: SearchCategories_search_edges_node[];
  tags: SearchProductTags_search_edges_node[];
  disabled: boolean;
  fetchMoreCategories: FetchMoreProps;
  fetchMoreCollections: FetchMoreProps;
  fetchMoreTags: FetchMoreProps;
  variants: ProductDetails_product_variants[];
  images: ProductDetails_product_images[];
  product: ProductDetails_product;
  header: string;
  saveButtonBarState: ConfirmButtonTransitionState;
  warehouses: WarehouseFragment[];
  stockOnMall: ProductVariant_productVariant_stockOnMall;
  taxTypes: TaxTypeFragment[];
  tenant: StaffTenantAssignment_tenant_mall;
  tenantId: string;
  tenantLabel: string;
  tenants?: TenantDropdownItem[];
  onTenantChange: (e) => void;
  productsUser: ProductsUser;
  validation: {
    basePrice: null;
    category: null;
    description: null;
    height: null;
    length: null;
    name: null;
    productStatus: null;
    sku: null;
    weight: null;
    width: null;
  };
  fetchCategories: (query: string) => void;
  fetchCollections: (query: string) => void;
  fetchTags: (query: string) => void;
  onFileSizeError: (type: string) => void;
  onVariantsAdd: () => void;
  onVariantShow: (id: string) => () => void;
  onVariantReorder: ReorderAction;
  onImageDelete: (id: string) => () => void;
  onSubmitVisibility?: any;
  onBack?();
  onDelete();
  onImageEdit?(id: string);
  onImageReorder?(event: { oldIndex: number; newIndex: number });
  onImageUpload(file: File);
  // onSeoClick?();
  onVariantAdd?();
  onSetDefaultVariant(variant: ProductDetails_product_variants);
  onWarehouseConfigure();
  onGenerateDeeplink(id: string);
  onSubmit?(data: ProductUpdatePageSubmitData);
}

export interface ProductUpdatePageSubmitData extends ProductUpdatePageFormData {
  attributes: ProductAttributeInput[];
  collections: string[];
  addStocks: ProductStockInput[];
  updateStocks: ProductStockInput[];
  removeStocks: string[];
}

export const ProductUpdatePage: React.FC<ProductUpdatePageProps> = ({
  // defaultWeightUnit,
  disabled,
  categories: categoryChoiceList,
  collections: collectionChoiceList,
  errors,
  fetchCategories,
  fetchCollections,
  fetchMoreCategories,
  fetchMoreCollections,
  images,
  header,
  placeholderImage,
  product,
  saveButtonBarState,
  variants,
  warehouses,
  stockOnMall,
  tags,
  taxTypes,
  tenant,
  onBack,
  onDelete,
  onImageDelete,
  onImageEdit,
  onImageReorder,
  onImageUpload,
  // onSeoClick,
  onSubmit,
  onSubmitVisibility,
  onVariantAdd,
  onVariantsAdd,
  onSetDefaultVariant,
  onVariantShow,
  onVariantReorder,
  onWarehouseConfigure,
  isChecked,
  selected,
  toggle,
  toggleAll,
  toolbar,
  onGenerateDeeplink,
  onFileSizeError,
  productsUser,
  brand,
  brands,
  brandLabel,
  onBrandChange,
  userView,
  userViews,
  userViewLabel,
  onUserViewChange,
  tenantId,
  tenantLabel,
  tenants,
  onTenantChange,
  fetchTags,
  fetchMoreTags,
  validation,
  ...props
}) => {
  const intl = useIntl();
  const localizeDate = useDateLocalize();
  const [selectedCategory, setSelectedCategory] = useStateFromProps(
    product?.category?.name || ""
  );

  const { tenantDropdown } = useStyles(props);

  const [selectedCollections, setSelectedCollections] = useStateFromProps(
    getChoices(maybe(() => product.collections, []))
  );

  const [selectedTaxType, setSelectedTaxType] = useStateFromProps(
    product?.taxType.description
  );

  const initialDescription = maybe<RawDraftContentState>(() =>
    JSON.parse(product.descriptionJson)
  );

  const [hasVariants, setHasVariants] = React.useState<boolean>(
    product?.isSimpleProduct || false
  );

  const categories = getChoices(categoryChoiceList);
  const collections = getChoices(collectionChoiceList);
  const tagsChoices = getChoices(tags);
  // const currency = product?.variants[0]?.price.currency;
  // let hasVariants = product?.productType?.hasVariants;
  const taxTypeChoices =
    taxTypes?.map(taxType => ({
      label: taxType.description,
      value: taxType.taxCode
    })) || [];

  let disabledOptions = [];
  let previousStatus = null;

  useEffect(() => {
    setHasVariants(!product?.isSimpleProduct);
  }, [product]);

  const getCurrencySymbol = (): string =>
    (0)
      .toLocaleString("en-US", {
        currency: "PHP",
        currencyDisplay: "symbol",
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
        style: "currency"
      })
      .replace(/\d/g, "")
      .trim();

  const currencySymbol = getCurrencySymbol();

  const breadcrumbDirectory = [
    {
      name: "Products",
      url: "/products"
    },
    {
      name: header
    }
  ];

  return (
    <ProductUpdateForm
      onSubmit={productsUser.isStoreAdmin ? onSubmitVisibility : onSubmit}
      product={product}
      categories={categories}
      collections={collections}
      selectedCollections={selectedCollections}
      setSelectedCategory={setSelectedCategory}
      setSelectedCollections={setSelectedCollections}
      setSelectedTaxType={setSelectedTaxType}
      stockOnMall={stockOnMall}
      taxTypes={taxTypeChoices}
      warehouses={warehouses}
      useMallVisibility={productsUser.isStoreAdmin}
    >
      {({ change, data, handlers, hasChanged, submit }) => {
        const filledDimensions = Boolean(
          data.length && data.height && data.width && data.weight
        );
        // get current product status
        if (previousStatus === null) {
          previousStatus = data.productStatus;
        }

        if (filledDimensions && images?.length > 1 && !hasVariants) {
          disabledOptions = disabledOptions.filter(
            status => status !== "ready"
          );
        }

        if (
          hasVariants &&
          images?.length > 0 &&
          product?.variants.length > 0 &&
          product?.variants[0].weight !== null
        ) {
          disabledOptions = disabledOptions.filter(
            status => status !== "ready"
          );
        }

        // check if product is eligible to be READY
        if (images && images.length === 0) {
          if (!disabledOptions.includes("ready")) {
            disabledOptions.push("ready");
          }
        }

        if (hasVariants && product?.variants.length === 0) {
          if (!disabledOptions.includes("ready")) {
            disabledOptions.push("ready");
          }
        }

        // remove UNLISTED item on disabledOptions
        if (!data.isPublishedByTenant) {
          disabledOptions = disabledOptions.filter(item => item !== "unlisted");
        }

        // DRAFT status can't be UNLISTED
        // product can't be UNLISTED if published by tenant is true
        // disabled DRAFT status if product current status is READY\UNLISTED
        if (
          product?.productStatus === "READY" ||
          product?.productStatus === "UNLISTED"
        ) {
          if (!disabledOptions.includes("draft")) {
            disabledOptions.push("draft");
          }
        }

        if (product?.productStatus === "DRAFT") {
          if (!disabledOptions.includes("unlisted")) {
            disabledOptions.push("unlisted");
          }
        }

        return (
          <>
            <Container>
              {productsUser.isKAMS && (
                <ProductKAMSDropdownGroup
                  brand={brand}
                  brandChoices={brands}
                  brandLabel={brandLabel}
                  brandDisabled={true}
                  onBrandChange={onBrandChange}
                  userView={userView}
                  userViewChoices={userViews}
                  userViewLabel={userViewLabel}
                  onUserViewChange={onUserViewChange}
                />
              )}
              <PageBreadcrumbNavigation items={breadcrumbDirectory} />
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  marginTop: "10px"
                }}
              >
                <PageBackNavigation onBack={onBack} />
                <PageHeader title={header} />
              </div>
              {productsUser.isKAMS && productsUser.isStoreAdmin && (
                <SingleAutocompleteSelectField
                  className={tenantDropdown}
                  label={intl.formatMessage({
                    defaultMessage: "Tenant & Mall"
                  })}
                  name="tenantDropdown"
                  displayValue={tenantLabel}
                  choices={tenants}
                  value={tenantId}
                  onChange={onTenantChange}
                />
              )}
              <Grid>
                <div>
                  <ProductDetailsForm
                    data={data}
                    disabled={disabled}
                    errors={errors}
                    initialDescription={initialDescription}
                    onChange={change}
                    isStoreAdmin={productsUser.isStoreAdmin}
                    fetchTags={fetchTags}
                    fetchMoreTags={fetchMoreTags}
                    tags={tagsChoices}
                    validate={validation}
                  />
                  <CardSpacer />
                  <ProductDeepLink
                    linkId={product && product.id}
                    linkUrl={product && product.deeplinkUrl}
                    shortLinkUrl={product && product.shortDynamicLink}
                    onGenerateDeeplink={onGenerateDeeplink}
                  />
                  <CardSpacer />
                  <ProductImages
                    images={images}
                    placeholderImage={placeholderImage}
                    onImageDelete={onImageDelete}
                    onImageReorder={onImageReorder}
                    onImageEdit={onImageEdit}
                    onImageUpload={onImageUpload}
                    isStoreAdmin={productsUser.isStoreAdmin}
                    onFileSizeError={onFileSizeError}
                  />
                  <CardSpacer />
                  {data.attributes.length > 0 && (
                    <ProductAttributes
                      attributes={data.attributes}
                      errors={errors}
                      disabled={disabled}
                      onChange={handlers.selectAttribute}
                      onMultiChange={handlers.selectAttributeMultiple}
                    />
                  )}
                  <CardSpacer />
                  <ProductVariants
                    disabled={disabled}
                    mall={tenant}
                    variants={variants}
                    product={product}
                    fallbackPrice={
                      product?.variants?.length
                        ? product.variants[0].price
                        : undefined
                    }
                    onRowClick={onVariantShow}
                    onVariantAdd={onVariantAdd}
                    onVariantReorder={onVariantReorder}
                    onSetDefaultVariant={onSetDefaultVariant}
                    toolbar={toolbar}
                    isChecked={isChecked}
                    isStoreAdmin={productsUser.isStoreAdmin}
                    selected={selected}
                    toggle={toggle}
                    toggleAll={toggleAll}
                    toggleVariant={thisToggle => {
                      setHasVariants(thisToggle);
                      change({
                        target: {
                          name: "isSimpleProduct",
                          value: !thisToggle
                        }
                      });
                    }}
                  />
                  {!hasVariants && (
                    <>
                      <CardSpacer />
                      <ProductDimension
                        data={data}
                        onFormDataChange={change}
                        title="Dimension"
                        isStoreAdmin={productsUser.isStoreAdmin}
                        validate={validation}
                      />
                      <CardSpacer />
                      <ProductPricing
                        currency={currencySymbol}
                        data={data}
                        disabled={disabled}
                        errors={errors}
                        onChange={change}
                        isStoreAdmin={productsUser.isStoreAdmin}
                        isSellerMaster={productsUser.isSellerMaster}
                        validate={validation}
                      />
                      <CardSpacer />
                      <ProductStocks
                        data={data}
                        disabled={disabled}
                        hasVariants={false}
                        errors={errors}
                        stocks={data.stocks}
                        mall={tenant}
                        isStoreAdmin={productsUser.isStoreAdmin}
                        warehouses={warehouses}
                        onChange={handlers.changeStock}
                        onFormDataChange={change}
                        onWarehouseStockAdd={handlers.addStock}
                        onWarehouseStockDelete={handlers.deleteStock}
                        onWarehouseConfigure={onWarehouseConfigure}
                        validate={validation}
                      />
                    </>
                    // ) : (
                    //   <>
                    //     <ProductShipping
                    //       data={data}
                    //       disabled={disabled}
                    //       errors={errors}
                    //       weightUnit={product?.weight?.unit || defaultWeightUnit}
                    //       onChange={change}
                    //     />
                    //     <CardSpacer />
                    //     <ProductStocks
                    //       data={data}
                    //       disabled={disabled}
                    //       hasVariants={false}
                    //       errors={errors}
                    //       stocks={data.stocks}
                    //       warehouses={warehouses}
                    //       onChange={handlers.changeStock}
                    //       onFormDataChange={change}
                    //       onWarehouseStockAdd={handlers.addStock}
                    //       onWarehouseStockDelete={handlers.deleteStock}
                    //       onWarehouseConfigure={onWarehouseConfigure}
                    //     />
                    //   </>
                  )}
                  <CardSpacer />
                  {/* <SeoForm
                  errors={errors}
                  title={data.seoTitle}
                  titlePlaceholder={data.name}
                  description={data.seoDescription}
                  descriptionPlaceholder={maybe(() =>
                    convertFromRaw(data.description)
                      .getPlainText()
                      .slice(0, 300)
                  )}
                  slug={data.slug}
                  slugPlaceholder={data.name}
                  loading={disabled}
                  onClick={onSeoClick}
                  onChange={change}
                  helperText={intl.formatMessage({
                    defaultMessage:
                      "Add search engine title and description to make this product easier to find"
                  })}
                /> */}
                  <CardSpacer />
                  {/* <Metadata data={data} onChange={handlers.changeMetadata} /> */}
                </div>
                <div>
                  <ProductOrganization
                    brand={maybe(() => product.brand)}
                    canChangeCategory={productsUser.isSellerMaster}
                    canChangeType={false}
                    categories={categories}
                    categoryInputDisplayValue={selectedCategory}
                    collections={collections}
                    collectionsInputDisplayValue={selectedCollections}
                    data={data}
                    disabled={disabled}
                    disabledCollection={productsUser.isSellerMaster}
                    errors={errors}
                    fetchCategories={fetchCategories}
                    fetchCollections={fetchCollections}
                    fetchMoreCategories={fetchMoreCategories}
                    fetchMoreCollections={fetchMoreCollections}
                    productType={product?.productType}
                    onCategoryChange={handlers.selectCategory}
                    onCollectionChange={handlers.selectCollection}
                    isStoreAdmin={productsUser.isStoreAdmin}
                    validate={validation}
                  />
                  <CardSpacer />
                  {!productsUser.isSellerMaster && (
                    <AvailabilityCard
                      data={data}
                      errors={errors}
                      disabled={disabled}
                      hasShopifyReference={
                        product && product.shopifyReference !== null
                      }
                      messages={{
                        hiddenLabel: intl.formatMessage({
                          defaultMessage: "Not published",
                          description: "product label"
                        }),
                        hiddenSecondLabel: intl.formatMessage(
                          {
                            defaultMessage: "will become published on {date}",
                            description: "product publication date label"
                          },
                          {
                            date: localizeDate(data.publicationDate, "L")
                          }
                        ),
                        visibleLabel: intl.formatMessage({
                          defaultMessage: "Published",
                          description: "product label"
                        })
                      }}
                      onChange={change}
                      isStoreAdmin={productsUser.isStoreAdmin}
                    />
                  )}
                  <CardSpacer />
                  {productsUser.isStoreAdmin ? null : (
                    <ProductStatus
                      data={data}
                      disabledOptions={disabledOptions}
                      onChange={change}
                    />
                  )}
                  <CardSpacer />
                  <ProductTaxes
                    data={data}
                    disabled={disabled}
                    selectedTaxTypeDisplayName={selectedTaxType}
                    taxTypes={taxTypes}
                    onChange={change}
                    onTaxTypeChange={handlers.selectTaxRate}
                  />
                </div>
              </Grid>
              <SaveButtonBar
                id="product"
                status={previousStatus}
                onCancel={onBack}
                onDelete={productsUser.isStoreAdmin ? undefined : onDelete}
                onSave={submit}
                state={saveButtonBarState}
                disabled={disabled || !hasChanged}
              />
            </Container>
          </>
        );
      }}
    </ProductUpdateForm>
  );
};
ProductUpdatePage.displayName = "ProductUpdatePage";
export default ProductUpdatePage;
